import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { ghs, toPesewas } from '../lib/money.js';
import { Button, Chip, Banner, Field } from '../components/ui.jsx';
import { Sheet, Divider, Section } from './SalesPoint.jsx';

const card = { background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' };
const Head = ({ children }) => (
  <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">{children}</div>
);
const Rowed = ({ children, tone }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--color-border-default)', background: tone }}>
    {children}
  </div>
);
const Empty = ({ children }) => <div style={{ padding: 40, textAlign: 'center' }} className="t-body-m muted">{children}</div>;

// ---------------------------------------------------------------- held drafts

export function Drafts({ slug, onResume }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [rows, setRows] = useState([]);
  const load = () => b.drafts().then(d => setRows(d.drafts)).catch(() => {});
  useEffect(() => { load(); }, [slug]);

  const total = d => d.payload.lines.reduce((a, l) => a + Math.max(0, l.unitPrice * l.qty - l.discount), 0);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="t-body-m secondary">
        Sales parked mid service. The client is under the dryer and the till is needed for someone else.
      </div>
      {!rows.length && <div style={card}><Empty>Nothing is being held.</Empty></div>}
      {rows.map(d => {
        const age = Math.round((Date.now() - new Date(d.created_at)) / 60000);
        const stale = age > 120;
        return (
          <div key={d.id} style={{ ...card, borderColor: stale ? 'var(--color-status-warning)' : undefined, padding: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div className="t-heading-s">{d.label}</div>
              <div className="t-body-s muted">
                {d.payload.lines.length} line{d.payload.lines.length === 1 ? '' : 's'}
                {d.held_by_name ? ` · held by ${d.held_by_name}` : ''}
              </div>
            </div>
            <Chip tone={stale ? 'warning' : 'neutral'} showDot={stale}>
              {age < 1 ? 'just now' : age < 60 ? `${age}m ago` : `${Math.floor(age / 60)}h ${age % 60}m ago`}
            </Chip>
            <span className="money t-money-l">{ghs(total(d))}</span>
            <Button size="m" onClick={() => b.dropDraft(d.id, 'resumed').then(() => onResume(d.payload))}>Resume</Button>
            <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
              onClick={() => b.dropDraft(d.id, 'discarded').then(load)}>Discard</Button>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- customers

export function Customers({ slug }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');
  const [adding, setAdding] = useState(false);
  const load = () => b.customers(q).then(d => setRows(d.customers)).catch(() => {});
  useEffect(() => { load(); }, [q, slug]);

  const shown = tab === 'debtors' ? rows.filter(c => c.balance > 0) : rows;
  const debtors = rows.filter(c => c.balance > 0).length;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button size="m" variant={tab === 'all' ? 'primary' : 'ghost'}
          style={tab === 'all' ? {} : { border: '1px solid var(--color-border-default)' }}
          onClick={() => setTab('all')}>All {rows.length}</Button>
        <Button size="m" variant={tab === 'debtors' ? 'primary' : 'ghost'}
          style={tab === 'debtors' ? {} : { border: '1px solid var(--color-border-default)' }}
          onClick={() => setTab('debtors')}>Debtors {debtors}</Button>
        <span style={{ flex: 1 }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by phone or name"
          style={{ width: 260, height: 44, padding: '0 14px', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)', font: '400 15px var(--font-ui)' }} />
        <Button size="m" onClick={() => setAdding(true)}>New customer</Button>
      </div>

      <div style={card}>
        <Head>NAME · PHONE · VISITS · LIFETIME SPEND · OWES</Head>
        {shown.map(c => (
          <Rowed key={c.id} tone={c.balance > 0 ? 'var(--color-status-danger-bg)' : undefined}>
            <span className="t-body-l" style={{ flex: 1 }}>{c.name}</span>
            <span className="money t-money-s secondary" style={{ width: 150 }}>{c.phone || ''}</span>
            <span className="money t-money-s secondary" style={{ width: 60, textAlign: 'right' }}>{c.visits}</span>
            <span className="money t-money-m" style={{ width: 160, textAlign: 'right' }}>{ghs(c.total_spend)}</span>
            <span className="money t-money-s" style={{ width: 140, textAlign: 'right', color: c.balance > 0 ? 'var(--color-status-danger)' : 'var(--color-text-muted)' }}>
              {ghs(c.balance)}
            </span>
          </Rowed>
        ))}
        {!shown.length && (
          <Empty>{tab === 'debtors'
            ? 'Nobody owes anything, which matches the POSA data where every credit balance is zero.'
            : 'No customer matches that. A walk-in needs no record at all.'}</Empty>
        )}
      </div>

      {adding && <NewCustomer slug={slug} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function NewCustomer({ slug, onClose, onSaved }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [form, setForm] = useState({ name: '', phone: '', note: '' });
  const [error, setError] = useState(null);
  return (
    <Sheet title="New customer" subtitle="A name is enough. Everything else can wait." onClose={onClose}>
      <Section label="DETAILS">
        <Field id="c-name" label="NAME" value={form.name} autoFocus onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <Field id="c-phone" label="PHONE" value={form.phone} hint="Used to look them up at the counter"
          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
        <Field id="c-note" label="NOTE" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
        {error && <Banner tone="danger">{error}</Banner>}
      </Section>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button style={{ flex: 1 }} disabled={!form.name.trim()}
          onClick={() => b.createCustomer(form).then(onSaved).catch(() => setError('That did not save.'))}>
          Save customer
        </Button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------- services

export function Services({ slug, canEdit }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [cat, setCat] = useState({ categories: [], services: [] });
  const [activeCat, setActiveCat] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const load = () => b.catalogue().then(setCat).catch(() => {});
  useEffect(() => { load(); }, [slug]);

  const shown = activeCat ? cat.services.filter(s => s.category_id === activeCat) : cat.services;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="t-body-m secondary">
          {cat.services.length} services in {cat.categories.length} categories
        </span>
        <span style={{ flex: 1 }} />
        {canEdit && <Button size="m" variant="secondary" onClick={() => setImporting(true)}>Import CSV</Button>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setActiveCat(null)} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
          <Chip tone={!activeCat ? 'accent' : 'neutral'} showDot={!activeCat}>All {cat.services.length}</Chip>
        </button>
        {cat.categories.map(c => (
          <button key={c.id} onClick={() => setActiveCat(c.id)} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
            <Chip tone={activeCat === c.id ? 'accent' : 'neutral'} showDot={activeCat === c.id}>{c.name} {c.count}</Chip>
          </button>
        ))}
      </div>

      <div style={card}>
        <Head>SERVICE · DURATION · COMMISSION · PRICE</Head>
        {shown.map(s => (
          <Rowed key={s.id}>
            <span className="t-body-m" style={{ flex: 1 }}>{s.name}</span>
            <span className="t-body-s muted" style={{ width: 80 }}>{s.duration_min ? `${s.duration_min} min` : ''}</span>
            <span className="t-body-s secondary" style={{ width: 110 }}>
              {s.commission_type ? `${s.commission_value}% override` : 'staff default'}
            </span>
            <span className="money t-money-m" style={{ width: 130, textAlign: 'right' }}>{ghs(s.price)}</span>
            {s.price_alt ? <Chip tone="neutral" showDot={false}>alt {ghs(s.price_alt)}</Chip> : null}
            {canEdit && <button className="btn btn-ghost btn-m" style={{ color: 'var(--color-accent-strong)' }} onClick={() => setEditing(s)}>Edit</button>}
          </Rowed>
        ))}
        {!shown.length && <Empty>Nothing in this category.</Empty>}
      </div>

      {editing && <ServiceEditor slug={slug} service={editing} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }} />}
      {importing && <ImportCsv slug={slug} onClose={() => setImporting(false)}
        onDone={() => { setImporting(false); load(); }} />}
    </div>
  );
}

function ServiceEditor({ slug, service, onClose, onSaved }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [price, setPrice] = useState((service.price / 100).toFixed(2));
  const [duration, setDuration] = useState(service.duration_min ?? '');
  const [rate, setRate] = useState(service.commission_value ?? '');
  const [error, setError] = useState(null);

  return (
    <Sheet title="Edit service" subtitle={service.name} onClose={onClose}>
      <Section label="PRICE AND DURATION">
        <Field id="s-price" label="PRICE, GHS" value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" />
        <Field id="s-dur" label="DURATION, MINUTES" value={duration} onChange={e => setDuration(e.target.value)} inputMode="numeric" />
      </Section>
      <Divider />
      <Section label="COMMISSION OVERRIDE">
        <Field id="s-rate" label="PERCENT OF LINE NET" value={rate} onChange={e => setRate(e.target.value)}
          hint="Leave empty to inherit each staff member's default rate" inputMode="decimal" />
        <Banner tone="info">
          Changing a price never rewrites a past sale. Every sale line keeps the price and the
          commission rule it was sold under.
        </Banner>
        {error && <Banner tone="danger">{error}</Banner>}
      </Section>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button style={{ flex: 1 }} onClick={() => b.updateService(service.id, {
          price: toPesewas(price),
          durationMin: duration === '' ? null : Number(duration),
          commissionType: rate === '' ? null : 'percent',
          commissionValue: rate === '' ? null : Number(rate)
        }).then(onSaved).catch(() => setError('That did not save.'))}>Save service</Button>
      </div>
    </Sheet>
  );
}

/** So 168 services get typed once, not twice. */
function ImportCsv({ slug, onClose, onDone }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [text, setText] = useState('Category,Name,Price,Duration\n');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const parsed = useMemo(() => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const head = lines[0].split(',').map(h => h.trim().toLowerCase());
    const idx = n => head.indexOf(n);
    return lines.slice(1).map(l => {
      const c = l.split(',').map(x => x.trim());
      return {
        category: c[idx('category')] || '',
        name: c[idx('name')] || '',
        price: c[idx('price')] || '',
        durationMin: idx('duration') >= 0 && c[idx('duration')] ? Number(c[idx('duration')]) : null
      };
    }).filter(r => r.name && r.category);
  }, [text]);

  return (
    <Sheet title="Import services" subtitle="Paste a CSV with Category, Name, Price and optionally Duration." onClose={onClose}>
      <Section label="CSV">
        <textarea value={text} onChange={e => setText(e.target.value)} rows={12}
          style={{
            width: '100%', padding: 12, borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border-strong)', background: 'var(--color-bg-surface)',
            color: 'var(--color-text-primary)', font: '400 13px/20px var(--font-money)', resize: 'vertical'
          }} />
        <div className="t-body-s muted">
          {parsed.length} row{parsed.length === 1 ? '' : 's'} ready. A price is read as cedis, so 300 means GHS 300.00.
          A service that already exists is updated rather than duplicated.
        </div>
        {result && (
          <Banner tone="success" title="Imported">
            {result.created} new, {result.updated} updated, {result.categoriesAdded} new categories.
          </Banner>
        )}
        {error && <Banner tone="danger">{error}</Banner>}
      </Section>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button style={{ flex: 1 }} disabled={!parsed.length}
          onClick={() => b.importServices(parsed).then(r => { setResult(r); onDone(); }).catch(() => setError('The import failed. Nothing was changed.'))}>
          Import {parsed.length} services
        </Button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------- staff

export function Staff({ slug, canEdit }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [rows, setRows] = useState([]);
  const [earnings, setEarnings] = useState({});
  const [editing, setEditing] = useState(null);
  const [cats, setCats] = useState([]);
  const [uncovered, setUncovered] = useState([]);

  const load = () => {
    b.catalogue().then(d => setCats(d.categories)).catch(() => {});
    b.staff().then(d => { setRows(d.staff); setUncovered(d.uncovered || []); }).catch(() => {});
    b.report('by-staff').then(d => {
      setEarnings(Object.fromEntries(d.rows.map(r => [r.staff_id, r])));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [slug]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="t-body-m secondary">
        {rows.length} staff. Staff records are separate from logins: most stylists never sign in and do not need to.
      </div>

      {uncovered.length > 0 && (
        <Banner tone="danger" title={`Nobody can perform ${uncovered.length} categor${uncovered.length === 1 ? 'y' : 'ies'}`}>
          {uncovered.map(c => `${c.name} (${c.services} services)`).join(', ')}. Nothing in
          {uncovered.length === 1 ? ' it' : ' them'} can be booked until someone is given the speciality.
        </Banner>
      )}
      <div style={card}>
        <Head>NAME · ROLE · COMMISSION · EARNED SO FAR</Head>
        {rows.map(s => {
          const e = earnings[s.id];
          return (
            <Rowed key={s.id}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="t-heading-s">{s.name}</span>
                  {s.user_id ? <Chip tone="accent">Has login</Chip> : null}
                </div>
                <div className="t-body-s muted">
                  {s.job_title} · {s.commission_value}% services · {s.product_commission_value}% retail
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {s.categoryIds?.length
                    ? s.categoryIds.map(id => (
                        <Chip key={id} tone="neutral" showDot={false}>
                          {cats.find(c => c.id === id)?.name || id}
                        </Chip>
                      ))
                    : <Chip tone="warning">Not specialised, can do anything</Chip>}
                </div>
              </div>
              <span className="t-body-s muted" style={{ width: 90, textAlign: 'right' }}>{e ? `${e.lines} lines` : ''}</span>
              <span className="money t-money-l" style={{ width: 150, textAlign: 'right' }}>{ghs(e?.commission ?? 0)}</span>
              {canEdit && <button className="btn btn-ghost btn-m" style={{ color: 'var(--color-accent-strong)' }} onClick={() => setEditing(s)}>Edit</button>}
            </Rowed>
          );
        })}
      </div>
      {editing && <StaffEditor slug={slug} member={editing} categories={cats}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function StaffEditor({ slug, member, categories, onClose, onSaved }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [rate, setRate] = useState(String(member.commission_value));
  const [product, setProduct] = useState(String(member.product_commission_value));
  const [picked, setPicked] = useState(new Set(member.categoryIds || []));
  const [error, setError] = useState(null);
  const example = Math.round((15000 * Number(rate || 0)) / 100);

  const toggle = id => setPicked(p => {
    const next = new Set(p);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  async function save() {
    try {
      await b.setStaffCategories(member.id, [...picked]);
      await b.updateStaff(member.id, {
        commissionType: 'percent', commissionValue: Number(rate), productCommissionValue: Number(product)
      });
      onSaved();
    } catch { setError('That did not save.'); }
  }

  return (
    <Sheet title={member.name} subtitle={member.job_title} onClose={onClose}>
      <Section label="SPECIALITIES">
        <div className="t-body-s muted">
          Only these appear when booking a service in that category. Choose none and this
          person can perform anything.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {categories.map(c => {
            const on = picked.has(c.id);
            return (
              <button key={c.id} onClick={() => toggle(c.id)}
                data-testid="speciality" data-name={c.name}
                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
                <Chip tone={on ? 'accent' : 'neutral'} size="m" showDot={on}>{c.name}</Chip>
              </button>
            );
          })}
        </div>
        {!picked.size && (
          <Banner tone="warning" title="No speciality set">
            {member.name} will be offered for every service. That is fine for someone who
            genuinely does everything, and wrong for everyone else.
          </Banner>
        )}
      </Section>
      <Divider />
      <Section label="DEFAULT COMMISSION">
        <Field id="st-rate" label="SERVICES, PERCENT OF LINE NET" value={rate} inputMode="decimal"
          onChange={e => setRate(e.target.value)} />
        <Field id="st-prod" label="RETAIL PRODUCTS, PERCENT" value={product} inputMode="decimal"
          onChange={e => setProduct(e.target.value)} />
        <Banner tone="info" title="What that means in practice">
          On a GHS 150.00 service with no discount, {member.name} earns {ghs(example)}. The rule and the
          amount are both written onto the sale line, so changing this never rewrites a past payout.
        </Banner>
        {error && <Banner tone="danger">{error}</Banner>}
      </Section>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button style={{ flex: 1 }} onClick={save}>Save</Button>
      </div>
    </Sheet>
  );
}
